/**
 * BridgeCore — bridge wave-2 item 3: the commanding instrument. The
 * bridge-scale (viewBox 480) sibling of ReactorCore, which SURVIVES
 * untouched as the compact/phone variant.
 *
 * PRIOR-ART ATTESTATION: none_exists — this is a genuinely new component.
 * It does NOT fork ReactorCore: every piece of geometry/stage math
 * (polar, arcPath, REACTOR_STAGES, TERMINAL_STATUSES, formatMinSec, ACCENT)
 * is IMPORTED from ../fleet/ReactorCore. Those helpers compute around the
 * local center (100, 100); this component wraps all layers in a single
 * <g transform="translate(140 140)"> so the same math lands on the 480
 * viewBox's center (240, 240) with zero duplication.
 *
 * Layer stack (plan docs/plans/2026-08-12-hud-bridge-wave2-plan.md §2,
 * radii in viewBox units around center 240 — i.e. local center 100):
 *   disc r=64 · readout · stage arcs r=96 sw=7 · [constellation slots
 *   r=122 / r=150 — item 4 injects here] · item ticks r=168–176 ·
 *   poll sweep r=190 · bezel r=204.
 *
 * Motion contract (same as wave-1): all animation is CSS. The only JS
 * timers are the 1s useNow elapsed ticker and the one-shot poll-phase
 * computation (negative animationDelay anchored to the real polledAt
 * timestamp, so the sweep reflects where in the poll cycle we actually
 * are). Down mood = gray + ZERO animation classes anywhere in the tree.
 * prefers-reduced-motion collapses every animation two ways: the CSS
 * media query below, AND a matchMedia hook that strips the animation
 * classes at render (the JS path is what the reduced-motion test sees).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FleetMood, MOOD_STYLE, useNow } from '../fleet/fleetMood';
import { parseTs } from '../fleet/fleetTime';
import {
  ACCENT,
  REACTOR_STAGES,
  TERMINAL_STATUSES,
  arcPath,
  formatMinSec,
  polar,
  type ReactorCoreOverview,
} from '../fleet/ReactorCore';
import { STAGE_AGENTS } from '../fleet/AssemblyLine';
import AgentConstellation from './AgentConstellation';
import BridgeDetail, { type SelectedInstrument } from './BridgeDetail';
import { BELT_AGENTS, buildConstellation } from './agentOrbit';

import { FleetSession } from '../../services/fleetHudApi';

interface BridgeCoreProps {
  mood: FleetMood;
  overview: ReactorCoreOverview;
  /** Active/latest run id — rendered truncated as the 4th readout line. */
  runId: string | null;
  /** overview.orchestrator_alive — drives the caption's ONLINE/DOWN half. */
  alive: boolean;
  /** Agent roster from the canonical source (FleetHud derives it from
      STAGE_AGENTS); only its LENGTH is rendered — never a hardcoded N. */
  roster: readonly string[];
  pollMs: number;
  polledAt: number | null;
  className?: string;
  /** Sessions for the active run — passed through to AgentConstellation. */
  sessions?: FleetSession[];
  /** Replay: overview.elapsed_s is the VIRTUAL clock — render it exactly,
      never wall-interpolate (a paused replay must not creep). */
  virtualElapsed?: boolean;
  /** Item 11: notified on every selection change (including null on close /
      unmount) so the page can apply/clear the spotlight class. This callback
      is how FleetHud RECEIVES the lifted selection — failure mode A. */
  onSelectionChange?: (selection: SelectedInstrument | null) => void;
}

/** Poll-sweep geometry: a short arc faked by dashing a FULL r=190 circle, so
    the element's fill-box center == the disc center and a CSS rotate() spins
    it about the true axis (a short arc path's bbox center is off-axis). */
const SWEEP_R = 190;
const SWEEP_CIRCUMFERENCE = 2 * Math.PI * SWEEP_R;
const SWEEP_ARC_LEN = 90; // ≈27° of arc

/** Item 11 anchor geometry — the same roster→slot map AgentConstellation
    renders, rebuilt here (pure, deterministic) to locate the popover anchor
    for a selected agent without reaching into the child. */
const CONSTELLATION_SLOTS = buildConstellation(STAGE_AGENTS, BELT_AGENTS);

/** polar() computes around local center (100,100); the centering translate
    lands it at (240,240) on the 480 viewBox → percentages of the wrapper. */
function anchorPct(local: { x: number; y: number }): { x: number; y: number } {
  return { x: ((local.x + 140) / 480) * 100, y: ((local.y + 140) / 480) * 100 };
}

/**
 * Wrapper sizing — clamp(340px, 56vh, 560px) per plan §2. Routed through a
 * custom property because jsdom's CSSOM silently DROPS clamp() from parsed
 * declarations but stores custom properties verbatim; real browsers resolve
 * var(--bc-core-size) to the identical clamp. Single source, test-observable.
 */
const WRAPPER_STYLE = {
  '--bc-core-size': 'clamp(340px, 56vh, 560px)',
  width: 'var(--bc-core-size)',
} as React.CSSProperties;

/**
 * Component-scoped CSS, bc- prefixed, mirroring the wave-1 rc- semantics one
 * layer up in scale. Mood-period-bound durations are inline styles; all
 * keyframes live here. The reduced-motion media query is the CSS half of the
 * collapse; the usePrefersReducedMotion hook below is the JS half.
 */
const BC_CSS = `
.bc-disc { transform-box: fill-box; transform-origin: center; }
.bc-disc--idle,
.bc-disc--waiting,
.bc-disc--building { animation-name: bc-pulse; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
.bc-disc--healing { animation-name: bc-pulse-heal; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
.bc-disc--halted { animation-name: bc-flicker; animation-timing-function: steps(2, end); animation-iteration-count: infinite; }
.bc-arc-current { animation: bc-march 1.2s linear infinite; }
.bc-sweep { animation-name: bc-spin; animation-timing-function: linear; animation-iteration-count: infinite; transform-box: fill-box; transform-origin: center; }
.bc-glow { animation-name: bc-breathe; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
@keyframes bc-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.72; transform: scale(1.045); } }
@keyframes bc-pulse-heal { 0%, 100% { opacity: 1; stroke: ${ACCENT}; } 50% { opacity: 0.7; stroke: #FF9933; } } /* design-law-allow (healing pulse secondary) */
@keyframes bc-flicker { 0% { opacity: 1; stroke: ${ACCENT}; } 50% { opacity: 0.5; stroke: #FF3300; } } /* design-law-allow (flicker warning color) */
@keyframes bc-march { to { stroke-dashoffset: -24; } }
@keyframes bc-spin { to { transform: rotate(360deg); } }
@keyframes bc-breathe { 0%, 100% { opacity: 0.8; transform: scale(1); } 50% { opacity: 1; transform: scale(1.06); } }
@media (prefers-reduced-motion: reduce) {
  .bc-root, .bc-root * { animation: none !important; }
}
`;

/** JS half of the reduced-motion collapse — reads the media query once and
    tracks changes. jsdom lacks matchMedia, so every access is guarded.
    Exported for item 4's AgentConstellation (same collapse contract). */
export function usePrefersReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setReduced(mql.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    return undefined;
  }, []);
  return reduced;
}

export default function BridgeCore({
  mood,
  overview,
  runId,
  alive,
  roster,
  pollMs,
  polledAt,
  className,
  sessions,
  onSelectionChange,
  virtualElapsed = false,
}: BridgeCoreProps) {
  const now = useNow(1000);
  const reducedMotion = usePrefersReducedMotion();

  // Item 11 — the LIFTED selection state. It lives here (never inside
  // AgentConstellation) so both the BridgeDetail popover and, via
  // onSelectionChange, the FleetHud spotlight see the same value.
  const [selectedInstrument, setSelectedInstrument] = useState<SelectedInstrument | null>(null);
  useEffect(() => {
    onSelectionChange?.(selectedInstrument);
  }, [selectedInstrument, onSelectionChange]);
  // If the core unmounts (e.g. compact toggle), clear the page's mirror so a
  // stale spotlight can never outlive the popover.
  useEffect(() => () => onSelectionChange?.(null), [onSelectionChange]);
  const closeDetail = useCallback(() => setSelectedInstrument(null), []);

  // Stage-arc click: select the stage, then flash + scrollIntoView the
  // matching band column. The column lookup is a data-stage-id ATTRIBUTE
  // MATCH — never nth-child (AssemblyLine renders a header row before the
  // columns). The flash is a real-event-bound one-shot and is omitted under
  // reduced motion; scrollIntoView fires either way.
  const selectStage = useCallback(
    (stage: string) => {
      setSelectedInstrument({ kind: 'stage', id: stage });
      const column = document.querySelector(`[data-stage-id="${stage}"]`);
      if (!(column instanceof HTMLElement)) return;
      if (typeof column.scrollIntoView === 'function') {
        column.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
      if (!reducedMotion) {
        column.classList.add('al-column--flash');
        window.setTimeout(() => column.classList.remove('al-column--flash'), 750);
      }
    },
    [reducedMotion]
  );

  // Popover anchor — the selected node's fixed polar slot, or the selected
  // stage arc's midpoint, in wrapper percentages.
  const detailAnchor = useMemo(() => {
    if (!selectedInstrument) return null;
    if (selectedInstrument.kind === 'agent') {
      const slot = CONSTELLATION_SLOTS.find((s) => s.agentType === selectedInstrument.id);
      return slot ? anchorPct(polar(slot.orbitRadius, slot.angleDeg)) : null;
    }
    const idx = REACTOR_STAGES.indexOf(
      selectedInstrument.id as (typeof REACTOR_STAGES)[number]
    );
    if (idx === -1) return null;
    return anchorPct(polar(96, (idx + 0.5) * (360 / REACTOR_STAGES.length)));
  }, [selectedInstrument]);

  // Elapsed ticker — same anchor-and-interpolate pattern as wave-1: anchor on
  // the poll-time elapsed_s, advance with the 1s useNow tick, re-anchor on
  // each new poll value. Replay (virtualElapsed): overview.elapsed_s IS the
  // virtual clock — no wall interpolation, or a PAUSED replay's readout
  // creeps forward in real time (K3 watchout, 2026-08-14).
  const anchorRef = useRef({ base: overview.elapsed_s, at: Date.now() });
  useEffect(() => {
    anchorRef.current = { base: overview.elapsed_s, at: Date.now() };
  }, [overview.elapsed_s]);
  const elapsedS = virtualElapsed
    ? Math.max(0, Math.floor(overview.elapsed_s))
    : Math.max(0, Math.floor(anchorRef.current.base + (now - anchorRef.current.at) / 1000));

  const pulseSeconds = MOOD_STYLE[mood].pulseSeconds;
  const glow = MOOD_STYLE[mood].glow;
  const isDown = mood === 'down';
  const isIdle = mood === 'idle';
  // The two motion kill-switches. Down mood and reduced motion BOTH strip
  // every animation class from the tree (the halted flicker and the poll
  // sweep included) — static render, zero keyframes bound.
  const motionOff = isDown || reducedMotion;

  // Poll-sweep phase: negative delay = how deep into the current poll cycle
  // we already are, anchored to the REAL polledAt. Recomputed only when a new
  // poll lands (polledAt changes) — never on the 1s tick, so the CSS clock
  // free-runs honestly between polls.
  const sweepDelayMs = useMemo(
    () => (polledAt === null ? 0 : -(Date.now() - polledAt)),
    [polledAt]
  );

  // Stage arcs — wave-1 logic verbatim: unknown stage → all upcoming.
  const stageIdx = REACTOR_STAGES.findIndex(
    (s) => s === (overview.stage ?? '').toUpperCase()
  );

  const items = overview.items_summary ?? [];

  // Four-line center readout (DM Mono). Line content per mood mirrors wave-1.
  let stageLine: string;
  let elapsedLine: string | null;
  if (isDown) {
    stageLine = 'OFFLINE';
    elapsedLine = null;
  } else if (isIdle) {
    stageLine = 'STANDBY';
    if (overview.last_run_at === null) {
      elapsedLine = 'never';
    } else {
      const ms = parseTs(overview.last_run_at);
      if (ms === null) {
        elapsedLine = 'never';
      } else {
        const ago = Math.max(0, Math.floor((now - ms) / 1000));
        elapsedLine = `last run ${Math.floor(ago / 3600)}h ${Math.floor((ago % 3600) / 60)}m ago`;
      }
    }
  } else {
    stageLine = (overview.stage ?? '').toUpperCase() || '—';
    elapsedLine = formatMinSec(elapsedS);
  }
  const runIdLine = runId ? (runId.length > 12 ? `${runId.slice(0, 12)}…` : runId) : '—';

  return (
    <div
      className={`bc-root relative flex flex-col items-center ${className ?? ''}`}
      style={WRAPPER_STYLE}
      data-testid="bridge-core"
    >
      {/* Ambient glow — one radial div BEHIND the SVG, bound to --mood-glow,
          breathing at the mood period. No class (no keyframes bound) when
          down, reduced-motion, or a zero-period mood. */}
      <div
        aria-hidden="true"
        className={`absolute inset-0${!motionOff && pulseSeconds > 0 ? ' bc-glow' : ''}`}
        style={{
          background: `radial-gradient(circle at center, var(--mood-glow, ${glow}) 0%, transparent 70%)`,
          animationDuration: `${pulseSeconds}s`,
        }}
        data-testid="bc-glow"
      />

      <svg
        viewBox="0 0 480 480"
        className="relative h-auto w-full"
        role="img"
        aria-label={`fleet bridge core — ${MOOD_STYLE[mood].label}`}
        data-testid="bc-svg"
      >
        <style>{BC_CSS}</style>

        {/* ReactorCore's math computes around local center (100,100); this
            single translate lands it on the 480 viewBox center (240,240). */}
        <g transform="translate(140 140)">
          {/* Bezel r=204 — static degree ticks, white 10%. Claims no state. */}
          {Array.from({ length: 60 }, (_, i) => {
            const deg = i * 6;
            const major = i % 5 === 0;
            const p0 = polar(major ? 198 : 201, deg);
            const p1 = polar(204, deg);
            return (
              <line
                key={`bezel-${i}`}
                x1={p0.x.toFixed(2)}
                y1={p0.y.toFixed(2)}
                x2={p1.x.toFixed(2)}
                y2={p1.y.toFixed(2)}
                stroke="#FFFFFF" // design-law-allow
                strokeOpacity={0.1}
                strokeWidth={major ? 1.5 : 1}
              />
            );
          })}

          {/* Poll sweep r=190 — one revolution per poll interval, phase
              anchored to the real polledAt via negative animationDelay. */}
          <circle
            cx="100"
            cy="100"
            r={SWEEP_R}
            fill="none"
            stroke={ACCENT}
            strokeOpacity={0.9}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={`${SWEEP_ARC_LEN} ${(SWEEP_CIRCUMFERENCE - SWEEP_ARC_LEN).toFixed(2)}`}
            className={!motionOff && polledAt !== null ? 'bc-sweep' : undefined}
            style={{
              animationDuration: `${pollMs}ms`,
              animationDelay: `${sweepDelayMs}ms`,
            }}
            data-testid="bc-sweep"
          />

          {/* Item ticks r=168–176 — wave-1 verbatim: one tick per real
              items_summary entry, lit on a terminal status. */}
          {items.map((entry, i) => {
            const deg = (i * 360) / items.length;
            const p0 = polar(168, deg);
            const p1 = polar(176, deg);
            const lit = !!entry.status && TERMINAL_STATUSES.has(entry.status);
            return (
              <line
                key={`tick-${i}`}
                x1={p0.x.toFixed(2)}
                y1={p0.y.toFixed(2)}
                x2={p1.x.toFixed(2)}
                y2={p1.y.toFixed(2)}
                stroke={lit ? ACCENT : 'rgba(255,255,255,0.2)'}
                strokeOpacity={lit ? 1 : 0.2}
                strokeWidth="2"
                strokeLinecap="round"
                data-testid={`bc-tick-${i}`}
                data-lit={lit ? 'true' : 'false'}
              />
            );
          })}

          {/* Constellation slots — AgentConstellation renders the stage-bound
              (r=122) and belt (r=150) agent nodes. Both live inside the
              centering transform so item 4 reuses polar(). */}
          <g data-slot="constellation-inner" data-radius="122">
            <AgentConstellation
              sessions={sessions}
              activeRunId={runId}
              mood={mood}
              onSelectAgent={(agentType) => setSelectedInstrument({ kind: 'agent', id: agentType })}
              selectedAgent={
                selectedInstrument?.kind === 'agent' ? selectedInstrument.id : null
              }
              spotlightActive={selectedInstrument !== null}
            />
          </g>
          <g data-slot="constellation-outer" data-radius="150" />

          {/* Stage arcs r=96 sw=7 — wave-1 verbatim: done solid, current
              marching-dash, upcoming 8% white. Item 11: clicking an arc
              selects the stage (popover + band-column flash/scroll). The arc
              deliberately carries NO data-stage-id — that attribute uniquely
              marks the AssemblyLine band column for the scroll lookup. */}
          {REACTOR_STAGES.map((stage, i) => {
            const state =
              stageIdx === -1 || i > stageIdx ? 'upcoming' : i === stageIdx ? 'current' : 'done';
            const selected =
              selectedInstrument?.kind === 'stage' && selectedInstrument.id === stage;
            return (
              <path
                key={stage}
                d={arcPath(96, i, REACTOR_STAGES.length)}
                fill="none"
                stroke={state === 'upcoming' ? '#FFFFFF' : ACCENT} // design-law-allow (white for upcoming state)
                strokeOpacity={state === 'upcoming' ? 0.08 : 1}
                strokeWidth="7"
                strokeLinecap="round"
                strokeDasharray={
                  state === 'current' ? '7 5' : state === 'upcoming' ? '2 6' : undefined
                }
                className={state === 'current' && !motionOff ? 'bc-arc-current' : undefined}
                style={{ cursor: 'pointer' }}
                onClick={() => selectStage(stage)}
                data-testid={`bc-arc-${stage}`}
                data-state={state}
                data-selected={selected ? 'true' : undefined}
              />
            );
          })}

          {/* Disc r=64 — wave-1 mood bindings scaled up. Halted → steps()
              flicker; down → gray and NO animation class of any kind. */}
          <circle
            cx="100"
            cy="100"
            r="64"
            fill={isDown ? 'rgba(255,255,255,0.10)' : glow}
            stroke={isDown ? 'rgba(255,255,255,0.30)' : ACCENT}
            strokeWidth="2.5"
            className={`bc-disc${motionOff ? '' : ` bc-disc--${mood}`}`}
            style={{
              animationDuration: `${pulseSeconds}s`,
              filter: isDown ? undefined : `drop-shadow(0 0 18px ${glow})`,
            }}
            data-testid="bc-disc"
          />

          {/* Center readout — DM Mono only; hierarchy via size/tracking/
              weight, never a display font. */}
          <text
            x="100"
            y="72"
            textAnchor="middle"
            className="font-mono"
            fontSize="9"
            letterSpacing="0.3em"
            fill="rgba(255,255,255,0.55)"
            data-testid="bc-readout-mood"
          >
            {MOOD_STYLE[mood].label}
          </text>
          <text
            x="100"
            y="97"
            textAnchor="middle"
            className="font-mono"
            fontSize="16"
            fontWeight="600"
            fill="rgba(255,255,255,0.92)"
            data-testid="bc-readout-stage"
          >
            {stageLine}
          </text>
          {elapsedLine !== null && (
            <text
              x="100"
              y="115"
              textAnchor="middle"
              className="font-mono"
              fontSize="11"
              fill="rgba(255,255,255,0.75)"
              data-testid="bc-readout-elapsed"
            >
              {elapsedLine}
            </text>
          )}
          <text
            x="100"
            y="131"
            textAnchor="middle"
            className="font-mono"
            fontSize="8"
            fill="rgba(255,255,255,0.40)"
            data-testid="bc-readout-runid"
          >
            {runIdLine}
          </text>
        </g>
      </svg>

      {/* Item 11 popover — absolutely positioned inside the relative wrapper,
          anchored to the selected node/arc via the same polar geometry. */}
      <BridgeDetail
        selection={selectedInstrument}
        anchor={detailAnchor}
        sessions={sessions}
        activeRunId={runId}
        reducedMotion={reducedMotion}
        onClose={closeDetail}
      />

      {/* Caption — N interpolated from the roster prop, never hardcoded. */}
      <div
        className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-text-muted"
        data-testid="bc-caption"
      >
        {alive ? 'ORCHESTRATOR ONLINE' : 'ORCHESTRATOR DOWN'} · {roster.length} AGENTS ·{' '}
        {REACTOR_STAGES.length} STAGES
      </div>
    </div>
  );
}
